import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../lib/api'
import { getUser, clearToken } from '../lib/auth'
import UpgradeModal from '../components/UpgradeModal'
import ChangePasswordModal from '../components/ChangePasswordModal'
import EditProfileModal from '../components/EditProfileModal'

export default function AccountPage() {
  const navigate = useNavigate()
  const [user, setUserState] = useState(getUser())
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [portalLoading, setPortalLoading] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => {
    api.get('/billing/subscription')
      .then((res) => setSubscription(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const handleManageSubscription = async () => {
    setPortalLoading(true)
    try {
      const res = await api.post('/billing/portal')
      window.location.href = res.data.url
    } catch {
      alert('Failed to open billing portal. Please try again.')
      setPortalLoading(false)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const handleProfileUpdated = (updatedUser) => {
    setUserState(updatedUser)
  }

  const handleDeleteAccount = async () => {
    setDeleteLoading(true)
    try {
      await api.delete('/signup')
      clearToken()
      navigate('/')
    } catch {
      alert('Failed to delete account. Please try again.')
      setDeleteLoading(false)
    }
  }

  if (loading) {
    return <div className="text-center text-gray-500 py-12">Loading...</div>
  }

  const isPremium = subscription?.premium
  const isCanceled = subscription?.status === 'canceled'
  const isActive = subscription?.status === 'active'

  return (
    <div className="max-w-lg mx-auto">
      <h1 className="text-2xl font-bold text-ink mb-6">Account</h1>

      <div className="bg-surface border-[1.5px] border-ink p-6 mb-6">
        <div className="flex justify-end mb-2">
          <button
            onClick={() => setProfileOpen(true)}
            className="text-xs text-muted hover:text-ink"
          >
            Edit
          </button>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Name</span>
            <span className="text-gray-900 font-medium">{user?.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Email</span>
            <span className="text-gray-900 font-medium">{user?.email}</span>
          </div>
          {user?.unconfirmed_email && (
            <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
              Pending confirmation: {user.unconfirmed_email}
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-gray-500">Password</span>
            <button
              onClick={() => setPasswordOpen(true)}
              className="text-sm text-accent hover:text-accent-dark font-medium"
            >
              Change Password
            </button>
          </div>
        </div>
      </div>

      <div className="bg-surface border-[1.5px] border-ink p-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Subscription</h2>

        {!isPremium && !isCanceled && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                Free
              </span>
            </div>
            <p className="text-sm text-gray-500 mb-4">1 vehicle, unlimited maintenance tracking</p>
            <button
              onClick={() => setUpgradeOpen(true)}
              className="w-full py-2 px-4 text-sm font-bold text-white bg-accent rounded-md hover:bg-accent-dark"
            >
              Upgrade to Premium
            </button>
          </div>
        )}

        {isActive && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-ink">
                Premium
              </span>
              <span className="text-sm text-gray-500 capitalize">{subscription.plan}</span>
            </div>
            <p className="text-sm text-gray-500 mb-4">Up to 99 vehicles + PDF export + attachments</p>
            <button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              className="w-full py-2 px-4 text-sm font-bold text-white bg-accent rounded-md hover:bg-accent-dark disabled:opacity-50"
            >
              {portalLoading ? 'Opening...' : 'Manage Subscription'}
            </button>
          </div>
        )}

        {isCanceled && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                Canceled
              </span>
            </div>
            {isPremium ? (
              <p className="text-sm text-gray-500 mb-4">
                Access until {formatDate(subscription.current_period_end)}
              </p>
            ) : (
              <p className="text-sm text-gray-500 mb-4">Your premium access has ended.</p>
            )}
            <button
              onClick={() => setUpgradeOpen(true)}
              className="w-full py-2 px-4 text-sm font-bold text-white bg-accent rounded-md hover:bg-accent-dark"
            >
              Re-subscribe
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 bg-surface border-[1.5px] border-accent p-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Delete Account</h2>
        {!deleteConfirm ? (
          <div>
            <p className="text-sm text-gray-500 mb-4">
              Permanently delete your account and all associated data including vehicles, maintenance records, and uploaded files.
            </p>
            <button
              onClick={() => setDeleteConfirm(true)}
              className="text-sm text-accent-dark hover:text-accent font-medium"
            >
              Delete my account
            </button>
          </div>
        ) : (
          <div>
            <p className="text-sm text-accent-dark mb-4">
              This action cannot be undone. All your data will be permanently deleted.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting...' : 'Yes, delete my account'}
              </button>
              <button
                onClick={() => setDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <EditProfileModal
        isOpen={profileOpen}
        onClose={() => setProfileOpen(false)}
        onUpdated={handleProfileUpdated}
      />

      <ChangePasswordModal
        isOpen={passwordOpen}
        onClose={() => setPasswordOpen(false)}
      />

      <UpgradeModal
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        reason="unlock all premium features"
      />
    </div>
  )
}
